// Logo component with mesh network design

interface LogoProps {
  className?: string;
}

export function Logo({ className = '' }: LogoProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="512"
      height="512"
      viewBox="-10 -10 276 276"
      fill="none"
      aria-label="open-me.sh • Mesh Network Logo"
      className={`logo-svg ${className}`}
      style={{ overflow: 'visible' }}
    >
      <title>open-me.sh • Mesh Network Logo</title>
      <desc>Interconnected mesh nodes with organic topology and pulsing animations</desc>
      
      <defs>
        {/* Gradient for connections */}
        <linearGradient id="connectionGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#0891b2', stopOpacity: 0.6 }} />
          <stop offset="100%" style={{ stopColor: '#22d3ee', stopOpacity: 0.8 }} />
        </linearGradient>
        
        {/* Simple shadow for nodes */}
        <filter id="nodeShadow">
          <feDropShadow dx="0" dy="0" stdDeviation="2" floodColor="#22d3ee" floodOpacity="0.8"/>
        </filter>
        
        {/* Pulse animation for connection waves */}
        <filter id="pulseGlow">
          <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      
      {/* Background glow layer (no animation) */}
      <g filter="url(#nodeShadow)" opacity="0.3">
        <circle cx="128" cy="128" r="10" fill="#22d3ee" />
        <circle cx="128" cy="68" r="8" fill="#22d3ee" />
        <circle cx="180" cy="98" r="8" fill="#22d3ee" />
        <circle cx="180" cy="158" r="8" fill="#22d3ee" />
        <circle cx="128" cy="188" r="8" fill="#22d3ee" />
        <circle cx="76" cy="158" r="8" fill="#22d3ee" />
        <circle cx="76" cy="98" r="8" fill="#22d3ee" />
      </g>

      {/* Pulsing wave rings (background) */}
      <g stroke="#22d3ee" fill="none" opacity="0.2" strokeWidth="1.5">
        <circle cx="128" cy="128" r="45" className="pulse-wave pulse-wave-1" />
        <circle cx="128" cy="128" r="70" className="pulse-wave pulse-wave-2" />
        <circle cx="128" cy="128" r="95" className="pulse-wave pulse-wave-3" />
      </g>

      {/* Connection lines forming mesh topology */}
      <g strokeLinecap="round" strokeLinejoin="round" opacity="1">
        {/* Center to surrounding nodes (radial spokes) */}
        <line x1="128" y1="128" x2="128" y2="68" stroke="url(#connectionGradient)" strokeWidth="2" className="mesh-line mesh-line-1" />
        <line x1="128" y1="128" x2="180" y2="98" stroke="url(#connectionGradient)" strokeWidth="2" className="mesh-line mesh-line-2" />
        <line x1="128" y1="128" x2="180" y2="158" stroke="url(#connectionGradient)" strokeWidth="2" className="mesh-line mesh-line-3" />
        <line x1="128" y1="128" x2="128" y2="188" stroke="url(#connectionGradient)" strokeWidth="2" className="mesh-line mesh-line-4" />
        <line x1="128" y1="128" x2="76" y2="158" stroke="url(#connectionGradient)" strokeWidth="2" className="mesh-line mesh-line-5" />
        <line x1="128" y1="128" x2="76" y2="98" stroke="url(#connectionGradient)" strokeWidth="2" className="mesh-line mesh-line-6" />
        
        {/* Outer hexagon ring (complete 6 sides) */}
        <line x1="128" y1="68" x2="180" y2="98" stroke="url(#connectionGradient)" strokeWidth="1.5" className="mesh-line mesh-line-7" opacity="0.85" />
        <line x1="180" y1="98" x2="180" y2="158" stroke="url(#connectionGradient)" strokeWidth="1.5" className="mesh-line mesh-line-8" opacity="0.85" />
        <line x1="180" y1="158" x2="128" y2="188" stroke="url(#connectionGradient)" strokeWidth="1.5" className="mesh-line mesh-line-9" opacity="0.85" />
        <line x1="128" y1="188" x2="76" y2="158" stroke="url(#connectionGradient)" strokeWidth="1.5" className="mesh-line mesh-line-10" opacity="0.85" />
        <line x1="76" y1="158" x2="76" y2="98" stroke="url(#connectionGradient)" strokeWidth="1.5" className="mesh-line mesh-line-11" opacity="0.85" />
        <line x1="76" y1="98" x2="128" y2="68" stroke="url(#connectionGradient)" strokeWidth="1.5" className="mesh-line mesh-line-12" opacity="0.85" />
      </g>
      
      {/* Mesh nodes - PURE SOLID NO FILTERS NO ANIMATIONS */}
      <g>
        {/* Center node (slightly larger, primary) */}
        <circle cx="128" cy="128" r="7" fill="#22d3ee" />
        
        {/* Surrounding nodes forming perfect hexagon (smaller, secondary) */}
        <circle cx="128" cy="68" r="6" fill="#22d3ee" />
        <circle cx="180" cy="98" r="6" fill="#22d3ee" />
        <circle cx="180" cy="158" r="6" fill="#22d3ee" />
        <circle cx="128" cy="188" r="6" fill="#22d3ee" />
        <circle cx="76" cy="158" r="6" fill="#22d3ee" />
        <circle cx="76" cy="98" r="6" fill="#22d3ee" />
      </g>
    </svg>
  );
}

